import { BaseDialog, Button, ConfirmDialog, GenericTable, Tabs } from '@app/components';
import CloseIcon from '@app/assets/icons/close.svg?react';
import BinIcon from '@app/assets/icons/bin.svg?react';
import PlusIcon from '@app/assets/icons/plus-white.svg?react';
import { MessageTemplateUsageEntry } from '@app/models/messageTemplates';
import { MessageTemplateChannel, MessageTemplateChannelLabels } from '@app/utils/consts';
import { capitalize } from 'lodash';
import { useMessageTemplates } from './MessageTemplates.hooks';
import { MessageTemplatesTableRow } from './MessageTemplatesTableRow';

const channelTabs = Object.values(MessageTemplateChannel).map((value) => ({ value, label: MessageTemplateChannelLabels[value] }));

const UsageList = ({ title, items }: { title: string; items: MessageTemplateUsageEntry[] }) =>
  items.length > 0 ? (
    <div>
      <p className="font-semibold text-sm mb-1">{title}</p>
      <ul className="text-sm text-base-davyGray space-y-0.5">
        {items.map((item) => (
          <li key={item.id}>
            {item.name} <span className="text-base-coolGray">({capitalize(item.status)})</span>
          </li>
        ))}
      </ul>
    </div>
  ) : null;

export const MessageTemplates = () => {
  const {
    canEdit,
    sendTestEmail,
    isSendingTest,
    channel,
    handleChannelChange,
    templatesData,
    isLoading,
    page,
    setPage,
    handleLimitChange,
    sortBy,
    sortOrder,
    handleSort,
    searchValue,
    setSearchValue,
    isSearching,
    createTemplate,
    openTemplate,
    duplicateTemplate,
    defaultTarget,
    setDefaultTarget,
    confirmSetDefault,
    isSettingDefault,
    deleteTarget,
    setDeleteTarget,
    deleteTargetDetails,
    isDeleteTargetLoading,
    confirmDelete,
    isDeleting,
  } = useMessageTemplates();

  // Lowercase 'email' reads better inside sentences, 'SMS' stays uppercase
  const channelName = channel === MessageTemplateChannel.SMS ? 'SMS' : 'email';
  const deleteUsage = deleteTargetDetails?.usage;
  const isDeleteTargetUsed = Boolean(deleteUsage && (deleteUsage.automations.length > 0 || deleteUsage.campaigns.length > 0));

  return (
    <>
      <div>
        <div className="bg-white border-b border-b-base-ghostWhite">
          <div className="container mx-auto pt-4 px-4 space-y-2">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <h2 className="font-bold text-[28px] mb-2">Templates</h2>
              {canEdit && (
                <Button onClick={createTemplate} leftNode={<PlusIcon />}>
                  Create {channelName} template
                </Button>
              )}
            </div>
            <Tabs tabs={channelTabs} onClick={handleChannelChange} defaultValue={channel} />
          </div>
        </div>

        <div className="bg-white">
          <div className="container mx-auto py-6 px-4 flex flex-col md:flex-row justify-between items-end md:items-center gap-4">
            <h3 className="text-2xl font-bold">
              {templatesData?.total ?? 0} {channelName} template{templatesData?.total === 1 ? '' : 's'}
            </h3>
            <div className="relative w-full max-w-md">
              <input
                type="text"
                className="w-full border-0 border-b border-gray-300 text-base sm:text-lg py-2 pr-10 focus:outline-none focus:ring-0 focus:border-gray-400 bg-transparent placeholder:text-gray-400"
                placeholder="Search"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                style={{ fontWeight: 400 }}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
                  <path stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M21 21l-3.5-3.5" />
                </svg>
              </span>
            </div>
          </div>
        </div>

        <div className="container mx-auto py-6 px-4">
          {templatesData && templatesData.total === 0 && (
            <div className="flex items-center justify-center flex-col max-w-[350px] mx-auto space-y-2 mt-20">
              <p className="font-bold">No templates found</p>
              <p className="text-sm text-base-coolGray text-center">{isSearching ? 'No templates match your search.' : 'There are no templates for this channel yet.'}</p>
            </div>
          )}

          <div className="bg-white overflow-auto">
            <GenericTable
              data={templatesData?.templates ?? []}
              currentPage={page}
              isLoading={isLoading}
              headers={[
                { label: 'Template', field: 'name', sortable: true },
                { label: 'Updated', field: 'updatedAt', sortable: true },
                { label: 'Used in', field: 'usageCount', sortable: false },
              ]}
              currentSortBy={sortBy}
              currentSortOrder={sortOrder}
              onSort={handleSort}
              renderRow={(rows) =>
                rows.map((row) => (
                  <MessageTemplatesTableRow
                    key={row.id}
                    data={row}
                    canEdit={canEdit}
                    onOpen={openTemplate}
                    onDuplicate={duplicateTemplate}
                    onSendTest={sendTestEmail}
                    isSendingTest={isSendingTest}
                    onSetDefault={setDefaultTarget}
                    onDelete={setDeleteTarget}
                  />
                ))
              }
              pagination={{
                total: templatesData?.total ?? 0,
                handleNextPage: setPage,
                handlePrevPage: setPage,
                handleLimitChange,
              }}
            />
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={Boolean(defaultTarget)}
        onClose={() => setDefaultTarget(undefined)}
        onConfirm={confirmSetDefault}
        isLoading={isSettingDefault}
        title="Make default template"
        message={`"${defaultTarget?.name}" will become the default ${channelName} template. Automations and campaigns that use the default template will send it from now on.`}
        confirmText="Make default"
      />

      <BaseDialog size="max-w-2xl" open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(undefined)}>
        <div className="border-b border-b-base-ghostWhite p-6 mb-6 flex gap-2 items-start justify-between">
          <div className="bg-base-antiFlash flex items-center p-2 rounded-lg">
            <BinIcon width={24} height={24} />
          </div>
          <div className="mr-auto my-auto">
            <h5 className="font-bold text-lg">Delete template</h5>
          </div>
          <button type="button" onClick={() => setDeleteTarget(undefined)}>
            <CloseIcon />
          </button>
        </div>
        <div className="px-6 pb-4 space-y-4">
          <p className="text-base-davyGray">Are you sure you want to delete "{deleteTarget?.name}"? This action can't be undone.</p>

          {isDeleteTargetLoading && <p className="text-sm text-base-coolGray">Checking where this template is used...</p>}

          {isDeleteTargetUsed && deleteUsage && (
            <div className="bg-gray-100 rounded-lg p-4 space-y-3">
              <p className="text-sm font-bold text-base-davyGray">These will switch to the default {channelName} template:</p>
              <UsageList title="Automations" items={deleteUsage.automations} />
              <UsageList title="Campaigns" items={deleteUsage.campaigns} />
            </div>
          )}

          <div className="flex items-center gap-4 justify-center py-4">
            <Button kind="secondary" className="w-full" type="button" onClick={() => setDeleteTarget(undefined)}>
              Cancel
            </Button>
            <Button kind="danger" className="w-full" type="button" disabled={isDeleting || isDeleteTargetLoading} onClick={confirmDelete}>
              {isDeleting ? 'Deleting...' : 'Delete template'}
            </Button>
          </div>
        </div>
      </BaseDialog>
    </>
  );
};
